import { useState } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents } from '../../lib/format';
import { EDITABLE_STATUSES } from './unitStatus';

/**
 * Edit one space: its street rate, its status and its notes.
 *
 * Shared by the operations screen and the units page. The two rules it carries
 * are the reason it is one component rather than two: an occupied space cannot
 * be re-statused here, and changing the rate under a sitting tenant does not
 * reprice them. Both are easy to omit when reimplementing, and neither failure
 * is visible until it has already happened.
 */
export default function EditUnitDialog({ unit, facilityId, onClose, onDone, onError }) {
  const [rate, setRate] = useState((unit.monthly_rate_cents / 100).toFixed(2));
  const [status, setStatus] = useState(unit.status);
  const [notes, setNotes] = useState(unit.notes ?? '');
  const [saving, setSaving] = useState(false);

  // A unit becomes occupied by moving a tenant in, never by setting a dropdown.
  const statusLocked = unit.status === 'occupied';

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.adminUpdateUnit(facilityId, unit.public_id, {
        monthly_rate: rate,
        ...(statusLocked ? {} : { status }),
        notes,
      });
      onDone();
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true"
         aria-label={`Edit unit ${unit.unit_number}`}>
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-slate-900">Unit {unit.unit_number}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <label className="field-label mb-1" htmlFor="rate">Street rate ($/month)</label>
        <input id="rate" type="number" step="0.01" min="0" required className="field-input" value={rate}
               onChange={(e) => setRate(e.target.value)} />
        {unit.lease_number && (
          <p className="mb-4 mt-1 rounded-lg bg-amber-50 p-2.5 text-[12px] leading-snug text-amber-900">
            {unit.first_name} {unit.last_name} is on {unit.lease_number} at{' '}
            <strong>{formatCents(unit.leased_rate_cents)}</strong>. Changing this does not reprice them —
            it applies to the next lease.
          </p>
        )}
        {!unit.lease_number && <div className="mb-4" />}

        <label className="field-label mb-1" htmlFor="status">Status</label>
        <select id="status" className="field-input mb-1" value={status} disabled={statusLocked}
                onChange={(e) => setStatus(e.target.value)}>
          {statusLocked
            ? <option value="occupied">occupied</option>
            : EDITABLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {statusLocked && (
          <p className="mb-4 text-[12px] text-slate-500">
            Occupied units change status through move-out, not here.
          </p>
        )}
        {!statusLocked && <div className="mb-4" />}

        <label className="field-label mb-1" htmlFor="notes">Notes</label>
        <textarea id="notes" rows={2} className="field-input mb-5" value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
